// Agent runs on a process map: hand a case (a customer PO) to the map's agents, and read what they did. The agent
// follows the revision in effect from the start, does the steps in agent lanes and stops at the first step a person
// does (apps/api/src/processes/agent.ts). Shown in the map's inspector; the runs list is the map's Runs view.
import { useEffect, useState } from "react";
import { byId, doneBy, outs, startId, type Released, type Step } from "@couragemfg/api/processes-model";
import { ApiError } from "../api";
import { ErrorBox } from "../ui";
import { bcUrl } from "../config";
import { fmtDate, runs, type ProcessDetail, type Run, type RunAction, type RunRow, type RunState, type RunStep } from "./processApi";

/** The steps the agents work on in a revision: from the start, up to the first one a person does. */
export function agentPlan(m: Released | null) {
  if (!m) return { steps: [] as Step[], stopsAt: null as Step | null };
  const B = byId(m), steps: Step[] = [], seen = new Set<string>();
  const first = startId(m);
  let cur = first ? outs(m, first)[0]?.to : undefined;
  while (cur && B[cur] && !seen.has(cur)) {
    seen.add(cur);
    const s = B[cur];
    if (s.type === "end" || doneBy(m, s) === "person") return { steps, stopsAt: s };
    steps.push(s); cur = outs(m, cur)[0]?.to;
  }
  return { steps, stopsAt: null as Step | null };
}

export const STATE_LABEL: Record<RunState, string> = { running: "Running", handed_off: "Handed off", done: "Done", stopped: "Stopped", failed: "Failed" };
export const STATE_TONE: Record<RunState, string> = { running: "", handed_off: "ok", done: "ok", stopped: "warn", failed: "bad" };
const STEP_LABEL: Record<RunStep["status"], string> = { received: "Received", done: "Done by the agent", waiting: "Waiting for a person", stopped: "Stopped", failed: "Failed" };
const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const bcOrder = (no: string) => `${bcUrl(9305)}&filter=${encodeURIComponent(`'No.' IS '${no}'`)}`;
const usd = (n: number | null) => (n === null ? "" : n.toLocaleString("en-US", { style: "currency", currency: "USD" }));

function actionText(a: RunAction) {
  const i: any = a.input ?? {};
  switch (a.tool) {
    case "find_customer": return `Looked up the customer “${i.query ?? ""}”`;
    case "find_item": return `Looked up the item “${i.query ?? ""}”`;
    case "find_quote": return i.quoteNumber ? `Looked up quote ${i.quoteNumber}` : `Looked for the customer's quote`;
    case "create_sales_order": return a.ok ? "Created the sales order in Business Central" : "Tried to create the sales order";
    case "choose_branch": return `Answered the decision: ${i.branch ?? ""}`;
    default: return a.tool;
  }
}

/** Start a run: paste the PO, or use a sample built from this company's BC data. */
export function RunStart({ d, onRun, onClose }: { d: ProcessDetail; onRun: (r: Run) => void; onClose: () => void }) {
  const plan = agentPlan(d.released);
  const [po, setPo] = useState("");
  const [sample, setSample] = useState<string | null>(null);
  const [busy, setBusy] = useState<"sample" | "run" | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const useSample = async () => {
    setBusy("sample"); setErr(null);
    try { const s = await runs.sample(d.id); setPo(s.text); setSample(s.customer); } catch (e) { setErr(e); } finally { setBusy(null); }
  };
  const go = async () => {
    setBusy("run"); setErr(null);
    try { onRun(await runs.start(d.id, po)); }
    catch (e) { if (e instanceof ApiError && e.body?.number) onRun(e.body as Run); else setErr(e); }
    finally { setBusy(null); }
  };
  const head = (
    <div className="pmap-p-head">
      <div className="row"><span className="pmap-eyebrow">Agent run · {d.docNo}{d.rev ? ` Rev ${d.rev}` : ""}</span><button className="btn pmap-sm" onClick={onClose}>Close</button></div>
      <h2 className="pmap-h2">Run the agent</h2>
    </div>
  );
  if (!d.released) return <>{head}<p className="pmap-note">Agents follow the revision in effect, and this map isn't released yet. Release it first.</p></>;
  if (!plan.steps.length) return <>{head}<p className="pmap-note">In Rev {d.rev}, the first step after the start{plan.stopsAt ? ` (${plan.stopsAt.name})` : ""} is done by a person, so there's nothing for an agent yet. Put that step in an agent lane and release the next revision.</p></>;
  return (
    <>
      {head}
      <p className="pmap-run-plan">The agent follows Rev {d.rev}: it does <b>{plan.steps.map((s) => s.name).join(", then ")}</b>{plan.stopsAt ? <>, then hands the case to <b>{d.released!.lanes.find((l) => l.id === plan.stopsAt!.lane)?.name ?? "a person"}</b> for {plan.stopsAt.name}.</> : "."}</p>
      <label className="pmap-fld"><span>Customer PO: paste the email or the EDI text</span>
        <textarea className="pmap-run-po" rows={12} value={po} maxLength={20000} disabled={busy === "run"} onChange={(e) => { setPo(e.target.value); setSample(null); }}
          placeholder={"Customer: …\nPO number: …\nShip by: …\n\n1. 10 x Part name (item no.) at $…"} /></label>
      {sample && <p className="pmap-note">A sample PO for {sample}, from your Business Central customers and items. One price is 8% over, so you'll see a flag.</p>}
      <ErrorBox error={err} />
      <div className="btn-row">
        <button className="btn primary" disabled={!po.trim() || !!busy} onClick={() => void go()}>{busy === "run" ? "Running…" : "Run the agent"}</button>
        <button className="btn" disabled={!!busy} onClick={() => void useSample()}>{busy === "sample" ? "Building a sample…" : "Use a sample PO"}</button>
      </div>
      <p className="pmap-note">{busy === "run" ? "The agent is working in Business Central. This can take up to a minute." : "In Business Central the agent works as you: your permissions apply, and you show on what it creates."}</p>
    </>
  );
}

/** One run: what the agent did at each step, what it made, and who has the case now. */
export function RunView({ number, initial, onClose, onSteps, onAgain }: { number: string; initial?: Run | null; onClose: () => void; onSteps: (ids: string[]) => void; onAgain: () => void }) {
  const [r, setR] = useState<Run | null>(initial ?? null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { if (initial?.number === number) { setR(initial); return; } setR(null); runs.get(number).then(setR, setErr); }, [number, initial]);
  useEffect(() => { if (r) onSteps(r.steps.map((s) => s.id).filter(Boolean)); }, [r]); // eslint-disable-line react-hooks/exhaustive-deps
  if (err) return <ErrorBox error={err} />;
  if (!r) return <div className="pmap-note">Loading the run…</div>;
  const flags = r.steps.reduce((a, s) => a + s.flags.length, 0);
  return (
    <>
      <div className="pmap-p-head">
        <div className="row"><span className="pmap-eyebrow">{r.number} · {r.docNo} Rev {r.rev}</span><button className="btn pmap-sm" onClick={onClose}>Close</button></div>
        <h2 className="pmap-h2">{r.title}</h2>
        <div className="pmap-run-meta"><span className={`pmap-status ${STATE_TONE[r.state]}`}>{STATE_LABEL[r.state]}</span><span>Started by {r.startedByName ?? "someone"}, {fmtTime(r.startedAt)}</span>{flags > 0 && <span className="pmap-run-flagcount">{flags} to check</span>}</div>
      </div>

      {r.handoff && (
        <div className={`pmap-handoff ${r.state === "handed_off" ? "" : "warn"}`}>
          <b>{r.state === "handed_off" ? "Next" : "A person takes over"}: {r.handoff.name}</b>
          <span>{r.handoff.lane}{r.handoff.owner && r.handoff.owner !== r.handoff.lane ? ` · ${r.handoff.owner}` : ""} · {r.handoff.wi}</span>
          {r.handoff.reason ? <p>{r.handoff.reason}</p> : r.handoff.instruction && <p>{r.handoff.instruction}</p>}
        </div>
      )}

      {r.records.length > 0 && (
        <div className="pmap-run-records">{r.records.map((x) => (
          <a key={x.id} className="pmap-modlink" href={bcOrder(x.number)} target="_blank" rel="noopener noreferrer">
            <span className="mono">{x.number}</span><span>Sales order for {x.customer}{x.amount !== null ? `, ${usd(x.amount)}` : ""}</span><span aria-hidden="true">↗</span>
          </a>))}</div>
      )}

      <ol className="pmap-run-steps">
        {r.steps.map((s, k) => (
          <li key={k} className={`st-${s.status}`}>
            <div className="h"><span className="n mono">{String(s.step).padStart(2, "0")}</span><b>{s.name}</b><span className="ln">{s.lane}</span></div>
            <div className="s"><span className={`pmap-run-chip ${s.status}`}>{STEP_LABEL[s.status]}</span>{s.summary}</div>
            {s.flags.length > 0 && <ul className="pmap-run-flags">{s.flags.map((f, i) => <li key={i}>{f}</li>)}</ul>}
            {s.actions.length > 0 && (
              <details className="pmap-run-actions"><summary>What the agent did ({s.actions.length})</summary>
                <ol>{s.actions.map((a, i) => <li key={i} className={a.ok ? "" : "no"}><span>{actionText(a)}</span><code>{a.result}</code></li>)}</ol>
              </details>
            )}
          </li>
        ))}
      </ol>

      <details className="pmap-run-actions"><summary>The PO as received</summary><pre className="pmap-run-input">{r.input.text}</pre></details>
      <p className="pmap-note">{r.model ? `Model ${r.model}, ${(r.inputTokens + r.outputTokens).toLocaleString()} tokens. ` : ""}Agents follow the revision in effect; this run followed Rev {r.rev}.</p>
      <div className="btn-row"><button className="btn" onClick={onAgain}>Run another PO</button></div>
    </>
  );
}

/** The Runs view: every run of this map, newest first. */
export function RunList({ d, sel, onOpen, onNew, reloadKey }: { d: ProcessDetail; sel: string | null; onOpen: (no: string) => void; onNew: () => void; reloadKey: number }) {
  const [rows, setRows] = useState<RunRow[] | null>(null);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => { runs.list(d.id).then(setRows, setErr); }, [d.id, reloadKey]);
  const plan = agentPlan(d.released);
  return (
    <div className="pmap-pane pmap-runs">
      <div className="pmap-runs-head">
        <div><h2>Agent runs</h2><p className="pmap-note">{plan.steps.length ? `Each run is one customer PO handed to the agent. It does ${plan.steps.map((s) => s.name).join(", ")} and hands the case to a person.` : "No step after the start is in an agent lane in the revision in effect, so there's nothing for an agent to run yet."}</p></div>
        {plan.steps.length > 0 && <button className="btn primary" onClick={onNew}>Run the agent</button>}
      </div>
      <ErrorBox error={err} />
      {!rows ? <div className="empty">Loading…</div> : !rows.length ? <div className="empty">No runs yet.</div> : (
        <div className="pmap-tscroll">
          <table className="pmap-table pmap-run-table">
            <thead><tr><th>Run</th><th>Case</th><th>Where it stands</th><th>BC</th><th>Started</th></tr></thead>
            <tbody>{rows.map((x) => (
              <tr key={x.number} className={sel === x.number ? "sel" : ""} onClick={() => onOpen(x.number)}>
                <td className="mono"><button className="pmap-linkish" onClick={(e) => { e.stopPropagation(); onOpen(x.number); }}>{x.number}</button></td>
                <td>{x.title}<div className="muted">Rev {x.rev}{x.flags ? ` · ${x.flags} to check` : ""}</div></td>
                <td><span className={`pmap-status ${STATE_TONE[x.state]}`}>{STATE_LABEL[x.state]}</span>{x.handoff && <div className="muted">{x.state === "handed_off" ? "Next" : "At"}: {x.handoff.name}</div>}</td>
                <td className="mono">{x.records.map((rec) => rec.number).join(", ") || "–"}</td>
                <td className="muted">{fmtDate(x.startedAt)}{x.startedByName ? ` · ${x.startedByName}` : ""}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
